import { startOfToday } from "date-fns";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

const useReleaseDistributionSchema = () => {
    const { t } = useTranslation();

    const required = t("messages.inputMsgs.required");

    const invalidDate = t("messages.inputMsgs.invalidDate");

    const asNullableDate = (msg) =>
        yup
            .date()
            .transform((oldValue, newValue) => (newValue === "" ? null : oldValue))
            .typeError(msg)
            .nullable();

    const today = startOfToday();

    return yup.object({
        availableForImmersiveDate: asNullableDate(invalidDate).notRequired(),
        client: yup.string().required(required),
        distributionType: yup.string(),
        fileType: yup.string().required(required),
        isApple: yup["boolean"]()["default"](false),
        isPreOrderWithIGT: yup["boolean"]()["default"](false),
        preOrderDate: asNullableDate(invalidDate)
            .when(["isApple", "preOrderType"], ([isApple, preOrderType], schema) =>
                isApple && !!preOrderType ? schema.required(required) : schema.notRequired(),
            )
            .test("pre-order-date-not-before-today", t("messages.inputMsgs.preOrderDateEarlierAndLater"), (value) => {
                if (!value) return true;

                return +value >= +today;
            })
            .test("pre-order-date-before-sales", t("messages.inputMsgs.preOrderDateEarlierAndLater"), function (value) {
                if (!value) return true;

                const { salesStartDate } = this.parent;

                if (!salesStartDate) return true;

                const salesStartDateValue = salesStartDate instanceof Date ? salesStartDate : new Date(salesStartDate);

                if (Number.isNaN(+salesStartDateValue)) return true;

                return +value < +salesStartDateValue;
            }),
        preOrderType: yup.string().test("preOrderType-required-if", required, function (value) {
            const { isApple, preOrderDate } = this.parent;

            if (isApple && !!preOrderDate) return !!value;

            return true;
        }),
        releaseDate: yup.lazy((_value, ctx) =>
            ctx.parent?.isApple
                ? yup
                      .mixed()
                      .oneOf([null])
                      .transform(() => null)
                      .nullable(true)
                      .notRequired()
                : asNullableDate(invalidDate).required(required),
        ),
        salesStartDate: yup.lazy((_value, ctx) =>
            ctx.parent?.isApple
                ? asNullableDate(invalidDate).required(required)
                : yup
                      .mixed()
                      .oneOf([null])
                      .transform(() => null)
                      .nullable(true)
                      .notRequired(),
        ),
        streamStartDate: yup.lazy((_value, ctx) =>
            ctx.parent?.isApple
                ? asNullableDate(invalidDate).required(required)
                : yup
                      .mixed()
                      .oneOf([null])
                      .transform(() => null)
                      .nullable(true)
                      .notRequired(),
        ),
        territory: yup.string().required(required),
        track: yup
            .mixed()
            .when(
                ["isApple", "isPreOrderWithIGT", "preOrderType"],
                ([isApple, isPreOrderWithIGT, preOrderType], schema) =>
                    isApple && isPreOrderWithIGT && !!preOrderType ? schema.required(required) : schema.notRequired(),
            ),
        trackFormat: yup.mixed().required(required),
    });
};

export default useReleaseDistributionSchema;
